<template>
  <eg-table-filters-base
    :available-filters="availableFilters"
    :all-available-filters="allAvailableFilters"
    :default-selected-filters="defaultSelectedFilters"
    :available-sorts="[...availableSorts, 'grade']"
    :show-add-filter="true"
    :can-remove-filters="true"
    :show-clear-all="true"
    :show-search="true"
    :show-select-all="showSelectAll"
    :selected-ids="selectedIds"
    :selected-all="selectedAll"
    :total="total"
    :limit="limit"
    :items-to-select-counts="itemsToSelectCounts"
    :is-filtered="isFiltered"
    :rubrics="rubrics"
    :data-testid="'essays-table-filters'"
    class="w-100 p-3 essays-table-filters"
    @filter-changed="$emit('filter-changed', $event)"
    @sort-changed="$emit('filter-changed', $event)"
    @selectedAll="toggleSelectedAll"
  >
    <!-- Select all slot for desktop view -->
    <template v-if="showSelectAll" #select-all>
      <div class="desktop divider"></div>
      <div v-if="selectedAll" class="desktop selected-text btn-sm">
        {{
          isFiltered
            ? $t("essays.allFilteredEssaysSelected")
            : $t("essays.allEssaysSelected")
        }}
      </div>
      <eg-b-button
        v-if="!selectedAll"
        size="sm"
        variant="link"
        class="desktop text-info"
        @click="toggleSelectedAll"
      >
        <template v-if="selectedAll">{{ $t("essays.deselect") }}</template>
        <template v-else>
          {{ $t("essays.selectAllCount", { total }) }}
          <span v-if="isFiltered">{{ $t("essays.thatMatchSearch") }}</span>
        </template>
      </eg-b-button>
    </template>

    <!-- Essay actions slot for tablet view -->
    <template v-if="selectedIds.length" #essay-actions>
      <div class="tablet essays-table-filters p-3">
        <div v-if="showSelectAll && selectedAll" class="selected-text btn-sm">
          {{
            isFiltered
              ? $t("essays.allFilteredEssaysSelected")
              : $t("essays.allEssaysSelected")
          }}
        </div>
        <eg-b-button
          v-if="showSelectAll && !selectedAll"
          size="sm"
          variant="link"
          class="text-info"
          @click="toggleSelectedAll"
        >
          <template v-if="selectedAll">{{ $t("essays.deselect") }}</template>
          <template v-else>
            {{ $t("essays.selectAllCount", { total }) }}
            <span v-if="isFiltered">{{ $t("essays.thatMatchSearch") }}</span>
          </template>
        </eg-b-button>

        <essay-actions
          class="ml-auto"
          :selected-all="selectedAll"
          :selected-ids="selectedIds"
          @export="$emit('export', $event)"
          @delete="$emit('delete', $event)"
        />
      </div>
    </template>
  </eg-table-filters-base>
</template>

<script>
import { getRubrics } from "@/api";
import parseRouteQuery from "@/utils/parseRouteQuery";
import { EgTableFiltersBase } from "@/components/global/eg/eg_table";
import EssayActions from "./essay_actions.vue";
import { isEqual } from "lodash";
import i18n from "@/i18n/i18n";

import {
  generateFilterName,
  removeFiltersFromLocalStorage,
} from "@/utils/localStorage";

const getAllAvailableFilters = () => {
  const filters = {
    reviewStatus: {
      title: i18n.t("essays.filters.reviewStatus"),
      key: "status",
      prepareValue: function (id) {
        return filters.reviewStatus.items.find((item) => item.id === id);
      },
      component: "filter-select",
      items: [
        { id: "Graded", name: i18n.t("essays.filters.notReviewed") },
        { id: "Reviewed", name: i18n.t("essays.filters.reviewed") },
      ],
    },
    isExported: {
      title: i18n.t("essays.filters.exportStatus"),
      key: "isExported",
      prepareValue: function (id) {
        return filters.isExported.items.find((item) => item.id === id);
      },
      component: "filter-select",
      items: [
        { id: "true", name: i18n.t("essays.filters.exported") },
        { id: "false", name: i18n.t("essays.filters.notExported") },
      ],
    },
    flags: {
      title: i18n.t("essays.plagiarism"),
      key: "flagType",
      prepareValue: function (id) {
        return filters.flags.items.find((item) => item.id === id);
      },
      component: "filter-select",
      items: [
        { id: "usedAI", name: i18n.t("essays.aiDetection") },
        { id: "plagiarism", name: i18n.t("essays.plagiarism") },
        {
          id: "closeToGrade",
          name: i18n.t("essays.filters.closeToPassingGrade"),
        },
        {
          id: "templateChecking",
          name: i18n.t("essays.filters.templateChecking"),
        },
      ],
    },
    gradeRange: {
      title: i18n.t("essays.filters.gradeRange"),
      key: ["gradeFrom", "gradeTo"],
      prepareValue: (value) => Number(value),
      component: "grade-range",
      defaultValue: {
        gradeFrom: 0,
        gradeTo: 50,
      },
    },
    name: {
      filterName: i18n.t("essays.filters.essayName"),
      title: i18n.t("essays.filters.essayNameContains"),
      key: "name",
      prepareValue: (value) => value.toString(),
      component: "input-filter",
    },
    class: {
      title: i18n.t("common.class"),
      key: "ClassId",
      prepareValue: (value) => Number(value),
      component: "class-select",
    },
    rubric: {
      title: i18n.t("common.rubric"),
      key: "RubricId",
      prepareValue: (value) => Number(value),
      component: "rubric-select",
    },
    createdAt: {
      title: i18n.t("essays.filters.gradedDateRange"),
      key: "createdAt",
      prepareValue: (value) => Number(value),
      component: "date-range",
    },
    studentName: {
      filterName: i18n.t("essays.filters.studentName"),
      title: i18n.t("essays.filters.studentNameContains"),
      key: "studentName",
      placeholder: i18n.t("essays.filters.searchStudent"),
      prepareValue: (value) => value.toString(),
      component: "input-filter",
    },
    studentId: {
      filterName: i18n.t("common.student"),
      title: i18n.t("common.student"),
      key: "studentId",
      prepareValue: (value) => Number(value),
      component: "student-select",
      placeholder: i18n.t("essays.filters.searchStudent"),
      isClearable: true,
      settings: {
        many: true,
        isSearch: true,
        selectAll: true,
      },
    },
    assignment: {
      title: i18n.t("common.assignment"),
      key: "AssignmentId",
      prepareValue: (value) => Number(value),
      component: "assignment-select",
    },
  };
  return filters;
};

export default {
  name: "EssaysTableFilters",
  components: {
    EgTableFiltersBase,
    EssayActions,
  },

  props: {
    selectedIds: {
      type: Array,
      default: () => [],
    },
    availableSorts: {
      type: Array,
      default: () => ["name", "createdAt", "studentName"],
    },
    availableFilters: {
      type: Array,
      default: () => ["rubric", "createdAt", "studentId"],
    },
    total: {
      type: Number,
      required: true,
    },
    limit: {
      type: Number,
      required: true,
    },
    itemsToSelectCounts: {
      type: Number,
      default: 10000,
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },
    selectedAll: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allAvailableFilters: getAllAvailableFilters(),
      defaultSelectedFilters: [
        { key: "reviewStatus", value: null, isActive: false },
      ],
      error: "",
      rubrics: [],
    };
  },

  computed: {
    showSelectAll() {
      return (
        this.total > this.limit &&
        this.selectedIds.length === this.itemsToSelectCounts
      );
    },

    routeQuery() {
      return this.$route?.query;
    },
  },

  watch: {
    routeQuery: {
      async handler(newQuery, oldQuery) {
        if (isEqual(newQuery, oldQuery)) {
          return;
        }

        const { filters } = parseRouteQuery(this.routeQuery);

        const hasFilters =
          filters &&
          Object.keys(filters).length > 0 &&
          Object.values(filters).some((v) => v !== null && v !== undefined);

        if (hasFilters) {
          this.$emit("filter-changed", { filters });
        }
      },
    },
  },

  created() {
    const filterName = generateFilterName({
      entity: "assignments",
      id: this.$route.params.id,
    });

    removeFiltersFromLocalStorage(filterName);

    this.fetchRubrics();
  },

  methods: {
    async fetchRubrics() {
      if (!this.availableFilters.includes("rubric")) {
        return;
      }

      try {
        const { data } = await getRubrics();
        this.rubrics = data;
      } catch (error) {
        this.error = error.response?.data?.error;
      }
    },

    toggleSelectedAll() {
      this.$emit("selectedAll", !this.selectedAll);
    },
  },
};
</script>
<style lang="scss" scoped>
.essays-table-filters {
  top: 57px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  border-top: 1px solid var(--color-stroke-neutral-default);
  border-bottom: 1px solid var(--color-stroke-neutral-default);
  z-index: 3;
  .desktop,
  &.desktop {
    @media (max-width: 768px) {
      display: none;
    }
  }

  .tablet,
  &.tablet {
    display: none;

    @media (max-width: 768px) {
      display: flex;
    }
  }

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 1rem;
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  & > .form-group {
    flex: 0 1 100%;
    max-width: 100%;

    @media (min-width: 768px) {
      flex: 0 1 215px;
      max-width: 215px;
    }
  }

  &::v-deep .btn-link:hover {
    text-decoration: unset;
  }

  // Custom styling for search input border on essays page only
  &::v-deep .search-input-wrapper .form-control {
    border-color: var(--color-stroke-neutral-default);
  }
}
</style>
